import React, { useMemo } from 'react';
import { byDate, marketEvents, parseIso, utc } from '../utils/marketCalendar';
import { todayEt } from '../utils/marketTime';
import { monthGrid } from '../utils/calendarGrid';
import type { EventKind, MarketEvent } from '../utils/marketCalendar';
import { useQuery } from '@tanstack/react-query';
import { ReportIcon } from '../components/TickerDetail/Earnings';
import { fetchEconomicCalendar, fetchEconomicReleases } from '../services/economicCalendarService';
import { fetchEarningsRange } from '../services/earningsCalendarService';
import type { EarningsEvent, ReportTime } from '../services/earningsCalendarService';
import type { EconomicEvent, Impact } from '../services/economicCalendarService';

/**
 * Calendar — what the market is scheduled to do.
 *
 * Expirations, holidays and early closes are computed from the exchange rules
 * (see `utils/marketCalendar.ts`) and are exact. Economic releases come from
 * FMP and earnings from Unusual Whales — both real, both still on dev proxies.
 */

/* ── chrome ─────────────────────────────────────────────────────────── */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Monday to Friday only. A trading calendar with two permanently empty
// columns wastes a fifth of the width to say "the market is shut", which the
// absence of the columns says more clearly.
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

/** Badge treatment per event kind. Colour carries the meaning; the tag names it. */
const KIND_STYLE: Record<EventKind, { label: string; className: string }> = {
  'quarterly-opex': { label: 'QUAD', className: 'bg-accent-yellow text-hot-tag-text' },
  'monthly-opex': { label: 'OPEX', className: 'bg-accent-yellow/20 text-accent-yellow border border-accent-yellow/40' },
  // Violet. bull-blue is the Up/Call colour everywhere else in the app, so a
  // blue VIX badge on a calendar of expirations invited a directional reading
  // it does not carry. Violet is the conventional volatility hue and collides
  // with nothing else here.
  'vix-expiry': { label: 'VIX M', className: 'bg-[#8b74c4]/20 text-[#a893da] border border-[#8b74c4]/50' },
  // Cyan, not a muted bull-blue. As a dimmer version of the monthly badge it
  // read as "a lesser VIX Monthly" rather than a different instrument, and at
  // 40 a year against 12 it is the one people see most. Same cyan the sector
  // heat map tops out at, so it is not a new colour in the app.
  'vix-weekly': { label: 'VIX W', className: 'bg-[#40b8e8]/15 text-[#40b8e8] border border-[#40b8e8]/40' },
  holiday: { label: 'CLOSED', className: 'bg-bear-red/20 text-bear-red border border-bear-red/40' },
  'early-close': { label: '½ DAY', className: 'bg-sunrise/20 text-sunrise border border-sunrise/40' },
};

const IMPACT_DOT: Record<Impact, string> = {
  high: 'bg-bear-red',
  medium: 'bg-sunrise',
  low: 'bg-gray-600',
};

/**
 * What the day cells are filled with.
 *
 * Holiday and early-close badges are in **every** view: a calendar that hid a
 * market closure depending on which tab you were on would be wrong rather than
 * merely different, and a company reporting into a half day matters.
 *
 * Expiration badges are dropped from the Earnings view. They are a different
 * subject — the options calendar rather than the reporting calendar — and in
 * a cell already carrying five names with three numbers each they compete for
 * the top line without informing anything below it.
 */
type CalendarView = 'consolidated' | 'economic' | 'earnings';

/** Badges that say something expires, as opposed to something about the session. */
const EXPIRATION_KINDS: ReadonlySet<EventKind> = new Set<EventKind>([
  'quarterly-opex',
  'monthly-opex',
  'vix-expiry',
  'vix-weekly',
]);

const VIEWS: Array<{ id: CalendarView; label: string; hint: string }> = [
  {
    id: 'consolidated',
    label: 'Overview',
    hint: 'Releases grouped into one line per report, plus the day’s biggest earnings',
  },
  {
    id: 'economic',
    label: 'Economic',
    hint: 'Every release the provider publishes, ungrouped, with actual / forecast / previous',
  },
  {
    id: 'earnings',
    label: 'Earnings',
    hint: 'Every reporting company, largest first',
  },
];

/** How many tickers a cell lists before it starts counting instead. */
const TICKER_LIMIT: Record<CalendarView, number> = {
  consolidated: 4,
  economic: 0,
  // The point of this view is the names, so the cap is only there to stop a
  // 300-reporter day from making one cell taller than the screen. Five of
  // these are the detailed rows below; the rest are ticker-only.
  earnings: 20,
};

/** How many of a day's names get the full treatment, largest cap first. */
const EARNINGS_DETAILED = 5;

/**
 * The grid needs more width for detailed rows.
 *
 * A ticker, an expected move and an estimate do not fit in the ~144px a
 * five-column 720px grid gives a cell. The card scrolls sideways rather than
 * squeezing, which is what the overflow container is already for.
 */
const GRID_MIN_W: Record<CalendarView, string> = {
  consolidated: 'min-w-[720px]',
  economic: 'min-w-[820px]',
  earnings: 'min-w-[1000px]',
};

function Badge({ kind, detail }: { kind: EventKind; detail?: string }): React.ReactElement {
  const { label, className } = KIND_STYLE[kind];
  return (
    <span
      title={detail}
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider leading-none ${className}`}
    >
      {label}
    </span>
  );
}

/* ── earnings detail ────────────────────────────────────────────────── */

const pct = (v: number) => `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;

/** Revenue, at the scale it is actually quoted. */
function money(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(0)}M`;
  return `${(v / 1e3).toFixed(0)}K`;
}

/**
 * When a company reports, in words.
 *
 * ⚠️ `unknown` is the MAJORITY case — the slot is unset for roughly two thirds
 * of rows upstream — so it is phrased as a fact about the schedule rather than
 * as an apology for missing data. "Time unconfirmed" is the normal state of an
 * earnings row, not a defect in it.
 */
const SESSION_LABEL: Record<ReportTime, string> = {
  bmo: 'before the open',
  amc: 'after the close',
  unknown: 'time unconfirmed',
};

/**
 * The session marker beside a ticker.
 *
 * `ReportIcon` draws a sun for `bmo` and a moon for `amc`, and renders NOTHING
 * for `unknown`. With unknown in the majority that left most of a day's names
 * with no marker and no explanation — indistinguishable from a rendering fault,
 * and easy to misread as "same as the one above". A dim dash fills the slot, so
 * every ticker carries a marker and the unconfirmed ones say so.
 *
 * ⚠️ The colours are not a binary. Sunrise is pre-market and Up/Call blue is
 * after the close, so anything that is not `bmo` must not be coloured blue by
 * default — that would paint the unknown majority as though it reported at
 * night.
 */
function SessionMark({ time, className = '' }: {
  time: ReportTime;
  className?: string;
}): React.ReactElement {
  if (time === 'unknown') {
    return (
      <span
        aria-hidden
        className={`inline-block w-3 text-center text-gray-600 leading-none ${className}`}
      >
        –
      </span>
    );
  }

  return (
    <ReportIcon
      time={time}
      className={`w-3 h-3 ${time === 'bmo' ? 'text-sunrise' : 'text-bull-blue'} ${className}`}
    />
  );
}

/**
 * One of the day's biggest reporters, in full.
 *
 * Reads as a forecast before the print and an outcome after it — the consensus
 * gives way to the reported number. Which one shows is decided by the data
 * rather than by the date, so a company that has reported early is not shown as
 * still pending.
 *
 * ⚠️ The expected move, the reaction and the pre-close have no upstream source
 * today and never populate, so in practice this renders as EPS and revenue
 * against their estimates. The conditionals stay because the fields are the
 * right shape and may be filled later — see the service header.
 */
function EarningsDetail({ e }: { e: EarningsEvent }): React.ReactElement {
  const reported = e.actualEps != null;
  const beat = reported && e.streetEst != null ? e.actualEps! >= e.streetEst : undefined;

  return (
    <div
      className="min-w-0"
      title={`${e.name ?? e.ticker} — ${SESSION_LABEL[e.time]}`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[11px] font-semibold text-accent-yellow leading-none">{e.ticker}</span>
        <SessionMark time={e.time} className="flex-none" />
        {/* The expected move belongs beside the session icon: both are about
            the print itself rather than its result, and reading "after the
            close, ±14.5%" as one phrase is the question this page answers.
            It stays visible after the print too — what was priced in is the
            only thing that makes the reaction below mean anything. */}
        {e.expectedMovePct != null && (
          <span className="font-numeric text-[10px] leading-none text-gray-300 whitespace-nowrap">
            ±{(e.expectedMovePct * 100).toFixed(1)}%
          </span>
        )}
        {/* Reaction is the one number here where colour is unambiguous: it is
            what the stock did, not whether the news was good. */}
        {reported && e.reaction != null && (
          <span
            className={`font-numeric text-[10px] font-semibold leading-none ml-auto ${
              e.reaction >= 0 ? 'text-bull-blue' : 'text-bear-red'
            }`}
          >
            {pct(e.reaction)}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-x-2 font-numeric text-[10px] leading-tight text-gray-400">
        {reported ? (
          <>
            {/* Labelled `eps` to match the `rev` line below. Unlabelled, two
                bare decimals and a "vs" gave no way to tell which figure was
                which at a glance. */}
            <span className="whitespace-nowrap">
              <span className="text-gray-500">eps </span>
              <span className={beat ? 'text-bull-blue' : 'text-bear-red'}>
                {e.actualEps!.toFixed(2)}
              </span>
              {e.streetEst != null && <span className="text-gray-500"> vs {e.streetEst.toFixed(2)}</span>}
            </span>
          </>
        ) : (
          <>
            {/* `eps est`, not a bare `est` — the revenue line says `rev est`,
                and one of them being unqualified made the pair read as though
                they measured the same thing. */}
            {e.streetEst != null && (
              <span className="whitespace-nowrap">
                <span className="text-gray-500">eps est </span>
                {e.streetEst.toFixed(2)}
              </span>
            )}
          </>
        )}
      </div>

      {/* Revenue on its own line, labelled — without the `rev` prefix a
          reader has no way to tell 90.01B from an EPS figure at a glance.
          This is the one line on the row that is FMP rather than UW.

          ⚠️ Whether a company has reported is decided ONCE, by `reported`
          above, and revenue follows it — it does not get its own opinion.
          FMP populates `revenue` for prints that have not happened yet (SPCX
          on 4 Aug 2026 carries an actual against a fiscal quarter ending 31
          March), so keying this line off `revenue != null` had the row
          claiming a result on the revenue line while the EPS line above it
          still showed an expected move. */}
      {(e.revenueEst != null || (reported && e.revenue != null)) && (
        <div className="font-numeric text-[10px] leading-tight whitespace-nowrap">
          <span className="text-gray-500">rev </span>
          {reported && e.revenue != null ? (
            <>
              <span
                className={
                  e.revenueEst != null
                    ? e.revenue >= e.revenueEst
                      ? 'text-bull-blue'
                      : 'text-bear-red'
                    : 'text-gray-300'
                }
              >
                {money(e.revenue)}
              </span>
              {e.revenueEst != null && (
                <span className="text-gray-500"> vs {money(e.revenueEst)}</span>
              )}
            </>
          ) : (
            <span className="text-gray-400">est {money(e.revenueEst!)}</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A release and its figures, as one line of hover text.
 *
 * The server used to send this pre-formatted; it now sends numbers and a unit,
 * so the string is assembled here instead. That is the better side of the
 * boundary for it — built from the same fields `Figure` renders inline, the
 * tooltip cannot disagree with the numbers printed under it, which a separate
 * server-formatted string eventually would.
 *
 * Absent figures are skipped rather than shown as dashes, matching `Figure`.
 * A release with none of them reads as just its title.
 */
function econTooltip(e: EconomicEvent): string {
  const figures = (
    [
      ['act', e.actual],
      ['est', e.estimate],
      ['prev', e.previous],
    ] as const
  )
    .filter(([, v]) => v != null)
    .map(([label, v]) => `${label} ${v}${e.unit ?? ''}`);

  return figures.length > 0 ? `${e.title} — ${figures.join(' · ')}` : e.title;
}

/** One figure and its label. Undefined renders as nothing, not as a dash. */
function Figure({ label, value, unit, strong }: {
  label: string;
  value?: number;
  unit?: string;
  strong?: boolean;
}): React.ReactElement | null {
  if (value == null) return null;
  return (
    <span className="whitespace-nowrap">
      <span className="text-gray-500">{label} </span>
      <span className={strong ? 'text-white font-semibold' : 'text-gray-300'}>
        {value}
        {unit}
      </span>
    </span>
  );
}

function DayCell({ date, month, events, earnings, economic, today, view }: {
  date: string;
  month: number;
  events: MarketEvent[];
  earnings: EarningsEvent[];
  economic: EconomicEvent[];
  today: string;
  view: CalendarView;
}): React.ReactElement {
  const d = parseIso(date);
  const outside = d.getUTCMonth() !== month;
  const isToday = date === today;
  const closed = events.some((e) => e.kind === 'holiday');

  // Closures stay in every view; expirations are the options calendar and
  // belong to the other two.
  const badges =
    view === 'earnings' ? events.filter((e) => !EXPIRATION_KINDS.has(e.kind)) : events;

  // Biggest names first, so a crowded day keeps the ones anyone cares about.
  // The server ranks by market cap with nulls last, so this only takes the top
  // of an already-ordered list rather than re-sorting it — the cap is populated
  // for 98.5% of rows in the optionable universe, but the ordering among the
  // rest is the server's to decide, not ours to guess at.
  const limit = TICKER_LIMIT[view];
  const names = earnings.slice(0, limit);
  const extra = earnings.length - names.length;

  // Only the Earnings view spends the vertical space on detail rows; the
  // Overview keeps its four tickers on one line under the releases.
  const detailCount = view === 'earnings' ? EARNINGS_DETAILED : 0;
  const detailed = names.slice(0, detailCount);
  const rest = names.slice(detailCount);

  return (
    <div
      className={`${
        view === 'economic' ? 'min-h-[190px]' : view === 'earnings' ? 'min-h-[230px]' : 'min-h-[150px]'
      } p-2 bg-dark-surface flex flex-col gap-1.5 ${
        outside ? 'opacity-40' : ''
      } ${closed ? 'bg-dark-surface-light' : ''}`}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <span
          className={`font-numeric text-sm font-semibold leading-none ${
            isToday
              ? 'bg-accent-yellow text-hot-tag-text font-bold rounded px-1.5 py-0.5'
              : 'text-white'
          }`}
        >
          {d.getUTCDate()}
        </span>
        {badges.map((e) => (
          <Badge key={`${e.kind}-${e.date}`} kind={e.kind} detail={e.detail} />
        ))}
      </div>

      {/* The name leads the tooltip: release names are long enough to truncate
          in a cell this width, and a clipped row otherwise had no way to say
          what it was. The figures follow it, since the Overview does not show
          them inline the way the Economic view does. */}
      {economic.map((e, i) => (
        <div
          // Rows are never grouped, so one release appears once per cut it was
          // published in — titles repeat within a day and cannot key a row on
          // their own.
          key={`${e.title}-${i}`}
          className="min-w-0"
          title={econTooltip(e)}
        >
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className={`w-1.5 h-1.5 rounded-full flex-none ${IMPACT_DOT[e.impact]}`} />
            <span className="text-[11px] text-gray-200 truncate">{e.title}</span>
            {e.time && (
              <span className="font-numeric text-[10px] text-gray-400 flex-none ml-auto">
                {e.time}
              </span>
            )}
          </div>

          {/* The figures, on the Economic view only.
              Deliberately NOT coloured against the forecast: a beat is good
              news for payrolls and bad news for CPI, so a green "above
              consensus" would be actively misleading half the time. The
              actual is simply the emphasised one. */}
          {view === 'economic' && (e.actual != null || e.estimate != null || e.previous != null) && (
            <div className="flex flex-wrap gap-x-2 pl-3 font-numeric text-[10px] leading-tight">
              <Figure label="act" value={e.actual} unit={e.unit} strong />
              <Figure label="est" value={e.estimate} unit={e.unit} />
              <Figure label="prev" value={e.previous} unit={e.unit} />
            </div>
          )}
        </div>
      ))}

      {names.length > 0 && (
        /* Hairline above the tickers: the badges and releases above are about
           the session itself, these are companies. Without a break the two ran
           together as one list. */
        /* Overview only: `mt-auto` pins the tickers to the bottom so every
           cell shares a baseline, and the hairline separates them from the
           releases above. On the Earnings view the names ARE the content —
           there is nothing above them to divide from, so a rule under the
           date read as a stray line, and pinning left a gap on any day with
           fewer names than the cell is tall. */
        <div
          className={`space-y-1.5 ${
            view === 'consolidated' ? 'mt-auto pt-1.5 border-t border-dark-border' : ''
          }`}
        >
          {/* The biggest names in full, then the rest as tickers. A day with
              300 reporters is normal, so the split is between the handful
              anyone is watching and the roll call. */}
          {detailed.map((e) => (
            <EarningsDetail key={e.ticker} e={e} />
          ))}

          {rest.length > 0 && (
            <div
              className={`flex flex-wrap gap-x-2 gap-y-1 ${
                detailed.length > 0 ? 'pt-1.5 border-t border-dark-border/60' : ''
              }`}
            >
              {rest.map((e) => (
                <span
                  key={e.ticker}
                  title={`${e.name ?? e.ticker} — ${SESSION_LABEL[e.time]}`}
                  className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-accent-yellow leading-none"
                >
                  {e.ticker}
                  {/* Sun and moon, the same glyphs and colours the Earnings tab
                      uses — sunrise for pre-market, Up/Call blue for after the
                      close, a dash when the slot is unconfirmed. */}
                  <SessionMark time={e.time} />
                </span>
              ))}
              {extra > 0 && (
                <span className="text-[11px] text-gray-400 leading-none">+{extra}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Which of the three fillings the day cells get. */
function ViewSelector({ view, onChange }: {
  view: CalendarView;
  onChange: (v: CalendarView) => void;
}): React.ReactElement {
  return (
    <div className="flex rounded border border-dark-border overflow-hidden">
      {VIEWS.map((v) => (
        <button
          key={v.id}
          type="button"
          onClick={() => onChange(v.id)}
          title={v.hint}
          className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider leading-none transition-colors ${
            view === v.id
              ? 'bg-accent-yellow text-hot-tag-text'
              : 'bg-dark-bg text-gray-400 hover:text-white'
          }`}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

/** A month step. Disabled is styled, not hidden — a missing arrow reads as a bug. */
function NavArrow({ dir, onClick, label, disabled }: {
  dir: 'prev' | 'next';
  onClick: () => void;
  label: string;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`flex-none w-6 h-6 flex items-center justify-center rounded border transition-colors ${
        disabled
          ? 'border-dark-border/50 bg-dark-bg text-gray-700 cursor-not-allowed'
          : 'border-dark-border bg-dark-bg text-gray-400 hover:text-white hover:border-gray-600'
      }`}
    >
      <svg viewBox="0 0 16 16" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2">
        <path
          d={dir === 'prev' ? 'M10 3 L5 8 L10 13' : 'M6 3 L11 8 L6 13'}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/* ── legend ─────────────────────────────────────────────────────────── */

/**
 * Legend, as a right-hand rail rather than a strip above the grid.
 *
 * Names each marker and stops. The derivation rules — third Fridays, the
 * thirty-day VIX count, which holiday moves what — live in
 * `utils/marketCalendar.ts`, which is where someone who needs them will look.
 * On the legend they were noise between the reader and the one thing they came
 * for, which is what the badge means.
 *
 * `detail` is for information the title cannot carry on its own, like the
 * early-close time. Most rows do not need one.
 */
function LegendRow({ swatch, title, detail }: {
  swatch: React.ReactNode;
  /** Omit where the badge already says it. */
  title?: string;
  detail?: string;
}): React.ReactElement {
  return (
    <li className="flex gap-2.5 items-baseline">
      <span className="flex-none w-14">{swatch}</span>
      <span className="min-w-0">
        {title && <span className="block text-[11px] text-white leading-tight">{title}</span>}
        {detail && (
          <span className="block text-[10px] text-gray-400 leading-snug mt-0.5">{detail}</span>
        )}
      </span>
    </li>
  );
}

function LegendGroup({ heading, children }: {
  heading: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    /* Boxed on the page background, one step down from the card the rail sits
       in. Four unboxed groups separated only by whitespace read as one long
       list — the headings have to compete with the entries above them to be
       seen as breaks. */
    <div className="rounded border border-dark-border bg-dark-bg p-3">
      <h3 className="text-[10px] uppercase tracking-wider text-gray-400 mb-2.5">{heading}</h3>
      <ul className="space-y-2.5">{children}</ul>
    </div>
  );
}

function Legend({ view }: { view: CalendarView }): React.ReactElement {
  // A key for markers the current view cannot show is worse than no key —
  // it sends the reader looking for something that is not there.
  const showEconomic = view !== 'earnings';
  const showEarnings = view !== 'economic';
  const showExpirations = view !== 'earnings';

  return (
    <div className="space-y-2.5">
      {showExpirations && (
      <LegendGroup heading="Expirations">
        {/* What expires, not when it is scheduled — the size of the roll-off
            is the reason to care, and the dating rules are in
            utils/marketCalendar.ts for anyone who needs them. */}
        <LegendRow
          swatch={<Badge kind="quarterly-opex" />}
          title="Quad witching"
          detail="Options and futures, index and single-stock. Biggest roll of the quarter"
        />
        <LegendRow
          swatch={<Badge kind="monthly-opex" />}
          title="Monthly"
          detail="Standard equity and index options, where most open interest sits"
        />
        <LegendRow swatch={<Badge kind="vix-expiry" />} title="VIX monthly" />
        <LegendRow swatch={<Badge kind="vix-weekly" />} title="VIX weekly" />
      </LegendGroup>
      )}

      {showEconomic && (
        <LegendGroup heading="Economic releases">
          {/* Low is filtered out of the grid in every view, so it is not in
              the key. */}
          {(['high', 'medium'] as Impact[]).map((impact) => (
            <LegendRow
              key={impact}
              swatch={<span className={`inline-block w-2 h-2 rounded-full ${IMPACT_DOT[impact]}`} />}
              title={`${impact[0].toUpperCase()}${impact.slice(1)} impact`}
            />
          ))}
          {view === 'economic' && (
            <LegendRow
              swatch={
                <span className="font-numeric text-[10px]">
                  <span className="text-gray-500">act </span>
                  <span className="text-white font-semibold">0.1</span>
                </span>
              }
              title="Actual, then forecast and previous"
              detail="Not coloured against the forecast — a beat is good news for payrolls and bad for CPI"
            />
          )}
        </LegendGroup>
      )}

      {showEarnings && (
      <LegendGroup heading="Earnings">
        <LegendRow
          swatch={
            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-accent-yellow">
              TICK <ReportIcon time="bmo" className="w-3 h-3 text-sunrise" />
            </span>
          }
          title="Before the open"
        />
        <LegendRow
          swatch={
            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-accent-yellow">
              TICK <ReportIcon time="amc" className="w-3 h-3 text-bull-blue" />
            </span>
          }
          title="After the close"
        />
        {/* Not a footnote: the slot is unset for about two thirds of companies,
            so this is the marker most names on the page carry. */}
        <LegendRow
          swatch={
            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-accent-yellow">
              TICK <SessionMark time="unknown" />
            </span>
          }
          title="Time unconfirmed"
          detail="The most common case — the company has a date but has not said which side of the session"
        />
        <LegendRow
          swatch={<span className="text-[11px] text-gray-400">+3</span>}
          title="More that day"
        />

        {/* Only the Earnings view renders detail rows, so the key for them
            only appears there. */}
        {view === 'earnings' && (
          <>
            {/* The expected move and the post-print reaction were documented
                here too. Neither has an upstream source, so both render as
                nothing on every row — and a legend entry for a symbol that
                never appears reads as data gone missing rather than data not
                offered. Restore these with the fields if QF ever fills them. */}

            {/* EPS and revenue share these two rows: same shape, same colour
                rule, two different measurements. Listing each metric twice
                said the same thing four times. */}
            <LegendRow
              swatch={
                <span className="font-numeric text-[10px]">
                  <span className="text-gray-500">est </span>
                  <span className="text-gray-400">1.24</span>
                </span>
              }
              title="Consensus"
              detail="Labelled eps est and rev est until the company reports"
            />
            <LegendRow
              swatch={
                <span className="font-numeric text-[10px]">
                  <span className="text-bull-blue">4.74</span>
                  <span className="text-gray-500"> vs 4.21</span>
                </span>
              }
              title="Reported vs consensus"
              detail="Labelled eps and rev. Colour is the beat or miss against the estimate beside it"
            />
          </>
        )}
      </LegendGroup>
      )}

      <LegendGroup heading="Market hours">
        <LegendRow swatch={<Badge kind="holiday" />} />
        <LegendRow swatch={<Badge kind="early-close" />} title="Closes 1:00 pm" />
      </LegendGroup>
    </div>
  );
}

/* ── page ───────────────────────────────────────────────────────────── */

export function CalendarPage(): React.ReactElement {
  // Eastern, not the viewer's clock — see todayEt. Using local time moved the
  // "today" highlight a day early every evening in the Americas.
  const today = todayEt();
  const now = parseIso(today);

  React.useEffect(() => {
    document.title = 'Calendar — Fuzion';
  }, []);

  /**
   * The month on screen, as an offset from the current one.
   *
   * An offset rather than a {year, month} pair so stepping past December is
   * ordinary addition and cannot produce month 12.
   *
   * **Never negative.** The calendar serves the current month forward only —
   * this is a page about what the market is *scheduled* to do, and a month
   * that has already happened is a different question. The back arrow is
   * disabled at 0 rather than removed, so the control stays where the eye
   * expects it.
   */
  const [offset, setOffset] = React.useState(0);
  const atStart = offset === 0;
  const [view, setView] = React.useState<CalendarView>('consolidated');

  const cursor = useMemo(() => {
    const d = utc(now.getUTCFullYear(), now.getUTCMonth() + offset, 1);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
  }, [now, offset]);

  const days = useMemo(() => monthGrid(cursor.year, cursor.month), [cursor]);
  // Both ends include the padding days that belong to neighbouring months, so
  // those cells are populated too rather than looking like quiet days.
  const from = days[0];
  const to = days[days.length - 1];

  const events = useMemo(() => byDate(marketEvents(from, to)), [from, to]);

  /**
   * The whole month in one request — the range endpoint replaced a per-day
   * bridge that cost ~42 requests for the same span.
   *
   * Held for the session: a company's reporting date does not move intraday.
   */
  const { data: earnings = [], isError: earningsError } = useQuery({
    queryKey: ['earningsRange', from, to],
    queryFn: () => fetchEarningsRange(from, to),
    staleTime: 6 * 60 * 60 * 1000,
  });

  /**
   * Economic releases — TWO endpoints for the same span, one per view.
   *
   * The Overview reads the consolidated route, where a CPI morning is one
   * `CPI / Inflation Rate` row instead of ten cuts of the same print. The
   * Economic view reads the raw route, which is every row as published. The
   * grouping is an opinion and it lives on the server, so Discord and the
   * morning report share it rather than each re-deriving it.
   *
   * ⚠️ They are NOT interchangeable. The consolidated route also DROPS a few
   * releases outright (CFTC positioning, the Treasury budget statement, the
   * Leading Index), so the Economic view is the only place they appear.
   *
   * Only the active view's query runs. Fetching both on mount would double the
   * calendar's requests to populate a grid the user may never switch to; the
   * cost is a brief load the first time they do, after which both are cached.
   *
   * The Earnings view needs neither, so on that view both sit disabled.
   *
   * Refetched on focus rather than cached hard: `actual` fills in as releases
   * print, and showing a forecast for a number that came out an hour ago is
   * worse than showing nothing.
   */
  const consolidatedQuery = useQuery({
    queryKey: ['economicCalendar', 'releases', from, to],
    queryFn: () => fetchEconomicReleases(from, to),
    enabled: view === 'consolidated',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const rawQuery = useQuery({
    queryKey: ['economicCalendar', 'raw', from, to],
    queryFn: () => fetchEconomicCalendar(from, to),
    enabled: view === 'economic',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  /**
   * Whichever query backs the current view. `EconomicRelease` widens to
   * `EconomicEvent` cleanly — it only adds `members`, which nothing below
   * reads — so the grid draws from one shape either way.
   *
   * Earnings resolves to neither. Both queries are disabled there, and a
   * disabled query reads as permanently pending rather than as done — so
   * selecting one anyway would leave `econLoaded` false and `econError` false
   * on a view where nothing was ever asked for. Null says that outright
   * instead of relying on a disabled query to be quiet.
   */
  const econQuery =
    view === 'consolidated' ? consolidatedQuery : view === 'economic' ? rawQuery : null;
  const econ = econQuery?.data;
  const econLoaded = econQuery?.isSuccess ?? false;
  const econError = econQuery?.isError ?? false;

  const earningsByDay = useMemo(() => {
    const map = new Map<string, EarningsEvent[]>();
    earnings.forEach((e) => map.set(e.date, [...(map.get(e.date) ?? []), e]));
    return map;
  }, [earnings]);

  const economicByDay = useMemo(() => {
    const map = new Map<string, EconomicEvent[]>();
    if (view === 'earnings') return map;

    // Low-impact releases are dropped in every view: there are several a day
    // and they crowd out the cells carrying a CPI or an FOMC.
    //
    // The server already filters to high+medium, so this normally removes
    // nothing. It is kept as a backstop rather than deleted as redundant —
    // if the `impact` parameter is ever renamed or dropped server-side, an
    // ignored filter comes back as a valid 200 full of low-impact rows, and
    // this is what keeps the grid readable until anyone notices.
    (econ?.events ?? [])
      .filter((e) => e.impact !== 'low')
      .forEach((e) => map.set(e.date, [...(map.get(e.date) ?? []), e]));
    return map;
  }, [econ, view]);

  // Closures in view, listed under the grid — the badge says a day is shut,
  // this says which holiday it is without needing a hover.
  const closuresInView = useMemo(
    () =>
      marketEvents(from, to)
        .filter((e) => e.kind === 'holiday' || e.kind === 'early-close')
        .map((e) => ({ date: e.date, detail: e.detail ?? e.label, early: e.kind === 'early-close' })),
    [from, to]
  );

  // Coverage runs to about T+120, so a far-enough month comes back genuinely
  // empty. Rather than cap the arrows at a date that moves every day, the grid
  // says so when a month has nothing in it.
  //
  // ⚠️ isSuccess, NOT isFetched. React Query marks a FAILED query as fetched,
  // so `isFetched` here would explain an unreachable feed as "no releases
  // published this month" — a confident and completely wrong answer. Only a
  // request that actually returned may claim the month is empty.
  //
  // ⚠️ This relies on the service throwing on a null envelope. The endpoint
  // reports its own failure as a 200 carrying an empty `events` and a null
  // `from`, which would otherwise land here as a successful, empty month.
  const noReleases = econLoaded && (econ?.events.length ?? 0) === 0;

  // Neither calendar feed is reachable. Stated once, above the grid, rather
  // than per-cell: 30 empty day cells each saying "unavailable" is noise.
  const feedsUnavailable = econError && earningsError;

  return (
    /* flex-1, not h-screen: the AppShell owns viewport height. */
    <div className="flex-1 min-h-0 bg-dark-bg flex flex-col overflow-hidden">
      <div className="flex-none flex items-center gap-3 h-10 px-4 bg-chrome border-b border-dark-border">
        <h1 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Calendar</h1>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        <section className="rounded-lg border border-dark-border bg-dark-surface">
          <div className="px-4 py-3 border-b border-dark-border flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <NavArrow
                dir="prev"
                onClick={() => setOffset((o) => Math.max(0, o - 1))}
                label={atStart ? 'Already at the current month' : 'Previous month'}
                disabled={atStart}
              />
              {/* Fixed width so the arrows hold still between a short month
                  name and a long one. */}
              <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px] text-center min-w-[150px]">
                {MONTHS[cursor.month]} <span className="font-numeric">{cursor.year}</span>
              </h2>
              <NavArrow dir="next" onClick={() => setOffset((o) => o + 1)} label="Next month" />
            </div>

            {/* Only once the view has moved. On the current month it would be
                a control that does nothing. */}
            {offset !== 0 && (
              <button
                type="button"
                onClick={() => setOffset(0)}
                className="px-2 py-1 rounded border border-dark-border bg-dark-bg text-[10px] font-bold uppercase tracking-wider text-gray-400 hover:text-white hover:border-gray-600 transition-colors"
              >
                Today
              </button>
            )}

            <ViewSelector view={view} onChange={setView} />

            {/* Every time on this page is Eastern — the exchange's clock, not
                the viewer's. Worth stating rather than leaving to be assumed
                by someone reading it from another timezone. */}
            <span className="text-[10px] uppercase tracking-wider text-gray-400">
              All times ET
            </span>

            {noReleases && (
              <span
                className="text-[10px] text-sunrise"
                title="The economic feed publishes to the end of the calendar year. Months beyond that carry expirations and holidays, which are computed, but no releases."
              >
                No releases published for this month yet
              </span>
            )}

            {/* Said once, here, rather than in each of thirty day cells.
                Expirations, holidays and early closes are computed from the
                exchange rules and are still exact — so the grid is useful even
                while the two live feeds are missing, and the message names
                precisely what is absent rather than condemning the page. */}
            {feedsUnavailable && (
              <span
                className="text-[10px] text-gray-400"
                title="These data sources are not connected yet. Expirations and holidays are computed locally and remain accurate."
              >
                Earnings and economic releases unavailable
              </span>
            )}

            {/* No sample chip here. Expirations, holidays and early closes are
                computed from the exchange rules and are exact; releases and
                earnings are both live feeds. */}

            {/* `ml-auto` rather than `justify-between` on the row: the controls
                to the left wrap, and justify-between would push them apart as
                soon as they did. This keeps the badge pinned right whatever the
                width. `order-last` holds that corner if the row wraps to two
                lines. Decorative — the wordmark is in the lockup, so alt is
                empty rather than duplicating it to a screen reader. */}
            <img
              src="/logo-badge.svg"
              alt=""
              aria-hidden="true"
              className="ml-auto order-last h-6 w-auto object-contain flex-none opacity-90"
            />
          </div>

          <div className="flex flex-col xl:flex-row">
            {/* 1px gap over a border-coloured background draws the grid rules,
                and keeps drawing them correctly when a month needs five rows
                instead of four. */}
            <div className="flex-1 min-w-0 p-px bg-dark-border overflow-x-auto">
              {/* min-w keeps five day cells legible: below this the rail was
                  winning the fight for width and the grid crushed. Past that
                  the card scrolls sideways rather than squeezing. */}
              <div className={`grid grid-cols-5 gap-px ${GRID_MIN_W[view]}`}>
                {WEEKDAY_LABELS.map((w) => (
                  <div
                    key={w}
                    className="bg-dark-surface-light px-2 py-2 text-[10px] uppercase tracking-wider text-gray-400 text-center"
                  >
                    {w}
                  </div>
                ))}
                {days.map((date) => (
                  <DayCell
                    key={date}
                    date={date}
                    month={cursor.month}
                    events={events.get(date) ?? []}
                    earnings={earningsByDay.get(date) ?? []}
                    economic={economicByDay.get(date) ?? []}
                    today={today}
                    view={view}
                  />
                ))}
              </div>
            </div>

            {/* Below the grid until there is width for a rail beside it. */}
            <aside className="flex-none w-full xl:w-64 p-4 border-t xl:border-t-0 xl:border-l border-dark-border">
              <Legend view={view} />
            </aside>
          </div>
        </section>

        {closuresInView.length > 0 && (
          <section className="rounded-lg border border-dark-border bg-dark-surface p-4">
            <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px] mb-3">
              Closures In View
            </h2>
            <ul className="space-y-1.5">
              {closuresInView.map((c) => (
                <li key={`${c.date}-${c.detail}`} className="flex items-baseline gap-3 text-xs">
                  <span className="font-numeric text-white w-20 flex-none">
                    {parseIso(c.date).toLocaleDateString('en-US', {
                      timeZone: 'UTC',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  <span className={c.early ? 'text-sunrise' : 'text-bear-red'}>
                    {c.early ? 'Early close' : 'Closed'}
                  </span>
                  <span className="text-gray-200">{c.detail}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
